// ============================================================================
// PATIENT SITES CARD — Bloque de centros con baja/alta multi-centro
// Respeta permisos por rol:
//  · ADMIN: puede operar sobre cualquier centro
//  · GESTOR_SEDE: solo sobre sus centros asignados
//  · USER: solo sobre el centro activo actual
// ============================================================================
import { useState } from 'react';
import { Building2, CheckCircle, XCircle, Plus } from 'lucide-react';
import type { LinkedSiteInfo } from '../../types/patient';
import { CollapsibleSection } from '../ui/CollapsibleSection';
import { ConfirmDialog } from '../ui/ConfirmDialog';
import { patientsApi } from '../../api/patients';
import { toast } from '../ui/Toast';
import { formatDate } from '../../utils/format';

type UserRole = 'USER' | 'GESTOR_SEDE' | 'ADMIN';

interface PatientSitesCardProps {
  patientId: string;
  patientName: string;
  linkedSites: LinkedSiteInfo[];
  currentSiteId: string;
  userRole: UserRole;
  userSiteIds: string[];
  userId?: string;
  onChanged: () => void;
  onRequestLinkNewSite?: () => void;
}

export function PatientSitesCard({
  patientId, patientName, linkedSites, currentSiteId,
  userRole, userSiteIds, userId,
  onChanged, onRequestLinkNewSite,
}: PatientSitesCardProps) {
  const [pendingToggle, setPendingToggle] = useState<LinkedSiteInfo | null>(null);
  const [isToggling, setIsToggling] = useState(false);

  const canManageSite = (siteId: string): boolean => {
    if (userRole === 'ADMIN') return true;
    if (userRole === 'GESTOR_SEDE') return userSiteIds.includes(siteId);
    return siteId === currentSiteId;
  };

  const canAddSite = userRole === 'ADMIN' || userRole === 'GESTOR_SEDE';

  const handleToggle = async () => {
    if (!pendingToggle) return;
    const target = pendingToggle;
    setIsToggling(true);
    try {
      if (target.siteId === currentSiteId) {
        if (target.isActive) {
          await patientsApi.delete(patientId);
          toast.success('Paciente dado de baja en este centro');
        } else {
          await patientsApi.reactivate(patientId);
          toast.success('Paciente reactivado en este centro');
        }
      } else {
        await patientsApi.updateAnySiteStatus(patientId, target.siteId, !target.isActive);
        toast.success(
          target.isActive
            ? `Paciente dado de baja en ${target.siteName}`
            : `Paciente reactivado en ${target.siteName}`,
        );
      }
      onChanged();
    } catch (err: any) {
      toast.error('Error', err?.message || 'No se pudo cambiar el estado');
    } finally {
      setIsToggling(false);
      setPendingToggle(null);
    }
  };

  const activeCount = linkedSites.filter((s) => s.isActive).length;

  const sorted = [...linkedSites].sort((a, b) => {
    if (a.siteId === currentSiteId) return -1;
    if (b.siteId === currentSiteId) return 1;
    if (a.isActive !== b.isActive) return a.isActive ? -1 : 1;
    return a.siteName.localeCompare(b.siteName, 'es');
  });

  return (
    <>
      <CollapsibleSection
        id="sites"
        title="Centros vinculados"
        icon={<Building2 className="h-5 w-5 text-gray-400" />}
        badge={
          <span className="text-sm font-normal text-gray-500">
            {activeCount} activos de {linkedSites.length}
          </span>
        }
        actions={
          canAddSite && onRequestLinkNewSite ? (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onRequestLinkNewSite(); }}
              className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-1.5"
            >
              <Plus className="h-4 w-4" /> Vincular centro
            </button>
          ) : undefined
        }
        defaultOpen={true}
        userId={userId}
      >
        {linkedSites.length === 0 ? (
          <p className="text-sm text-gray-500 italic">Sin centros asignados.</p>
        ) : (
          <div className="space-y-3">
            {sorted.map((site) => {
              const canManage = canManageSite(site.siteId);
              const isCurrent = site.siteId === currentSiteId;
              return (
                <div
                  key={site.siteId}
                  className={`flex items-center justify-between gap-3 p-3 rounded-lg border ${
                    site.isActive
                      ? isCurrent
                        ? 'border-blue-200 bg-blue-50'
                        : 'border-green-200 bg-green-50'
                      : 'border-gray-200 bg-gray-50'
                  }`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-gray-900 truncate">{site.siteName}</span>
                      {site.siteCode && (
                        <span className="text-xs text-gray-500 font-mono">({site.siteCode})</span>
                      )}
                      {isCurrent && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 font-medium">
                          Centro activo
                        </span>
                      )}
                      {site.isActive ? (
                        <CheckCircle className="h-4 w-4 text-green-500" />
                      ) : (
                        <XCircle className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                    {site.medicalRecordNumber && (
                      <p className="text-xs text-blue-700 font-medium mt-0.5">
                        NHC: {site.medicalRecordNumber}
                      </p>
                    )}
                    <p className="text-xs text-gray-500 mt-0.5">
                      Vinculado: {formatDate(site.linkedAt)}
                    </p>
                  </div>

                  <div className="flex items-center gap-2 shrink-0">
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      site.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-600'
                    }`}>
                      {site.isActive ? 'Activo' : 'Inactivo'}
                    </span>
                    {canManage && (
                      <button
                        type="button"
                        onClick={() => setPendingToggle(site)}
                        className={`text-xs px-2.5 py-1 rounded-md border font-medium transition-colors ${
                          site.isActive
                            ? 'border-amber-300 text-amber-700 hover:bg-amber-100'
                            : 'border-green-300 text-green-700 hover:bg-green-100'
                        }`}
                      >
                        {site.isActive ? 'Dar de baja' : 'Reactivar'}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CollapsibleSection>

      <ConfirmDialog
        isOpen={!!pendingToggle}
        onClose={() => !isToggling && setPendingToggle(null)}
        onConfirm={handleToggle}
        isLoading={isToggling}
        variant={pendingToggle?.isActive ? 'warning' : 'info'}
        title={pendingToggle?.isActive ? `¿Dar de baja en ${pendingToggle.siteName}?` : `¿Reactivar en ${pendingToggle?.siteName}?`}
        message={
          pendingToggle?.isActive
            ? `"${patientName}" será dado de baja en "${pendingToggle.siteName}". Sus datos se preservan y podrás reactivarlo más tarde.`
            : `"${patientName}" será reactivado en "${pendingToggle?.siteName}".`
        }
        confirmText={pendingToggle?.isActive ? 'Dar de baja' : 'Reactivar'}
      />
    </>
  );
}

export default PatientSitesCard;
